import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import {
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  Paper,
  CircularProgress,
  Divider,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse
} from '@mui/material';
import { toast } from 'react-toastify';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';

const Profile = () => {
  const { user, updateProfile } = useAuth();
  const isBusinessUser = user?.role === 'business';
  
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    password: '',
    confirmPassword: '',
    phone: user?.phone || '',
    address: user?.address || '',
    businessName: user?.businessDetails?.businessName || '',
    businessCategory: user?.businessDetails?.category || '',
    businessDescription: user?.businessDetails?.description || ''
  });
  
  const [businessCategories, setBusinessCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [error, setError] = useState('');
  
  const { 
    name, email, password, confirmPassword,
    phone, address, businessName, businessCategory, businessDescription
  } = formData;
  
  // Fetch business categories
  useEffect(() => {
    if (isBusinessUser) {
      const fetchCategories = async () => {
        setLoadingCategories(true);
        try {
          const response = await api.get('/api/users/business-categories');
          setBusinessCategories(response.data || []);
        } catch (error) {
          console.error('Error fetching business categories:', error);
          // Fallback categories
          setBusinessCategories([
            'Barber Shop',
            'Hospital',
            'Auto Service Center', 
            'Dental Care Center', 
            'Fitness Studio', 
            'Hair & Style Salon', 
            'Legal Consultancy', 
            'Spa & Massage Center', 
            'Tech Repair Shop',
            'Restaurant',
            'Other'
          ]);
        } finally {
          setLoadingCategories(false);
        }
      };
      
      fetchCategories();
    }
  }, [isBusinessUser]);
  
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };
  
  const validateForm = () => {
    if (!name || !email) {
      setError('Name and email are required');
      return false;
    }
    
    if (password && password !== confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    
    if (password && password.length < 6) {
      setError('Password must be at least 6 characters');
      return false;
    }
    
    if (isBusinessUser && !businessName) {
      setError('Business name is required');
      return false;
    }
    
    if (isBusinessUser && !businessCategory) {
      setError('Business category is required');
      return false;
    }
    
    return true;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    
    try {
      // Prepare user data for update
      const userData = {
        name,
        email,
        phone,
        address,
        ...(password && { password })
      };
      
      // Add business details if business user
      if (isBusinessUser) {
        userData.businessDetails = {
          businessName,
          category: businessCategory,
          description: businessDescription || '',
          website: user?.businessDetails?.website || ''
        };
      }
      
      const success = await updateProfile(userData);
      
      if (success) {
        toast.success('Profile updated successfully');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      setError('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        My Profile
      </Typography>
      
      <Paper sx={{ p: 3, mt: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Avatar sx={{ width: 80, height: 80, mr: 2, bgcolor: isBusinessUser ? 'secondary.main' : 'primary.main' }}>
            {isBusinessUser ? <BusinessIcon sx={{ fontSize: 50 }} /> : <PersonIcon sx={{ fontSize: 50 }} />}
          </Avatar>
          <Box>
            <Typography variant="h5">{user?.name}</Typography>
            <Typography variant="body1" color="text.secondary">
              {user?.email}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Account Type: {isBusinessUser ? 'Business' : user?.role === 'admin' ? 'Administrator' : 'Customer'}
            </Typography>
            {isBusinessUser && user?.businessDetails?.category && (
              <Typography variant="body2" color="text.secondary">
                Category: {user.businessDetails.category}
              </Typography>
            )}
          </Box>
        </Box>
        
        <Divider sx={{ my: 3 }} />
        
        <Typography variant="h6" gutterBottom>
          Edit Profile Information
        </Typography>
        
        {error && (
          <Typography color="error" variant="body2" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}
        
        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                id="name"
                name="name"
                label="Full Name"
                value={name}
                onChange={handleChange}
                required
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                id="email"
                name="email"
                label="Email Address"
                value={email}
                onChange={handleChange}
                required
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                id="phone"
                name="phone"
                label="Phone Number"
                value={phone}
                onChange={handleChange}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                id="address"
                name="address"
                label="Address"
                value={address}
                onChange={handleChange}
              />
            </Grid>
            
            {/* Business Information (only shown if role is business) */}
            <Collapse in={isBusinessUser} sx={{ width: '100%' }}>
              <Grid container spacing={3} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle1" gutterBottom>
                    Business Information
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    required={isBusinessUser}
                    fullWidth
                    name="businessName"
                    label="Business Name"
                    id="businessName"
                    value={businessName}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required={isBusinessUser}>
                    <InputLabel id="business-category-label">Business Category</InputLabel>
                    <Select
                      labelId="business-category-label"
                      id="businessCategory"
                      name="businessCategory"
                      value={businessCategory}
                      label="Business Category"
                      onChange={handleChange}
                      disabled={loadingCategories}
                    >
                      {businessCategories.map((category) => (
                        <MenuItem key={category} value={category}>
                          {category}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="businessDescription"
                    label="Business Description"
                    id="businessDescription"
                    multiline
                    rows={3}
                    value={businessDescription}
                    onChange={handleChange}
                  />
                </Grid>
              </Grid>
            </Collapse>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle1" gutterBottom>
                Change Password (leave blank to keep current password)
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                id="password"
                name="password"
                label="New Password"
                type="password"
                value={password}
                onChange={handleChange}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                id="confirmPassword"
                name="confirmPassword"
                label="Confirm New Password"
                type="password"
                value={confirmPassword}
                onChange={handleChange}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : 'Save Changes'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Box>
  );
};

export default Profile; 